import { FISCAL_STATUSES } from './prro.types';

type RecordData = Record<string, unknown>;
const object = (value: unknown): RecordData => value && typeof value === 'object' && !Array.isArray(value) ? value as RecordData : {};
const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value : null;
const number = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Explicit instants use the Kyiv provider clock, as do compact dates in the local shift journal. */
export function historyDate(value: unknown): string {
  if (typeof value !== 'string') return '—';
  if (/(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '—';
    return new Intl.DateTimeFormat('uk-UA', {
      timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(date).replace(',', '');
  }
  const parts = /^(\d{4})-?(\d{2})-?(\d{2})[T ]?(\d{2}):?(\d{2})(?::?\d{2})?/.exec(value);
  return parts ? `${parts[3]}.${parts[2]}.${parts[1]} ${parts[4]}:${parts[5]}` : '—';
}

export function historyItem(record: RecordData, shift: boolean) {
  const operation = object(record['local_operation']);
  const type = text(record['type']) ?? '';
  const kinds: Record<string, { title: string; icon: string }> = {
    chk: { title: 'prro.receipt', icon: 'receipt_long' },
    sale: { title: 'prro.task.1', icon: 'receipt_long' },
    return: { title: 'prro.task.2', icon: 'assignment_return' },
    z_report: { title: 'prro.task.11', icon: 'summarize' },
    x_report: { title: 'prro.task.10', icon: 'summarize' },
    cash_in: { title: 'prro.task.3', icon: 'savings' },
    cash_out: { title: 'prro.task.4', icon: 'payments' },
  };
  let status = 'prro.history_external';
  let color = '#64748b';
  let statusIcon = 'cloud_done';
  if (shift) {
    status = text(record['date_closed']) ? 'prro.closed' : record['date_closed'] === null ? 'prro.open' : 'prro.unknown';
    color = status === 'prro.open' ? '#15803d' : '#64748b';
    statusIcon = status === 'prro.open' ? 'lock_open' : 'lock';
  } else if (FISCAL_STATUSES.some((value) => value === operation['status'])) {
    status = `prro.status.${operation['status']}`;
    const succeeded = operation['status'] === 'succeeded';
    const stopped = ['manual_required', 'retry_required', 'collision', 'unknown'].includes(String(operation['status']));
    color = succeeded ? '#15803d' : stopped ? '#b45309' : '#64748b';
    statusIcon = succeeded ? 'check_circle' : stopped ? 'error_outline' : 'schedule';
  }
  const link = text(record['document_url']);
  const report = type === 'z_report' || type === 'x_report';
  return {
    record, shift, report,
    title: shift ? 'prro.shift' : kinds[type]?.title ?? 'prro.history_document',
    icon: shift ? 'event_note' : kinds[type]?.icon ?? 'description',
    status, color, statusIcon,
    reference: text(record['fiscal_number']),
    localNumber: number(record['local_number']),
    date: historyDate(shift ? record['date_started'] : record['date_created']),
    closedAt: historyDate(record['date_closed']),
    amount: number(record['sum']) ?? number(record['amount']),
    balance: number(record['cash_closed']) ?? number(record['cash_current']),
    startingBalance: number(record['cash_started']),
    offline: typeof record['is_offline'] === 'boolean' ? record['is_offline'] : null,
    operationId: text(operation['id']),
    url: !report && link && /^https:\/\//i.test(link) ? link : null,
  };
}
export type HistoryItem = ReturnType<typeof historyItem>;
